import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KatanaPathDumpRow } from '@/types';
import { formatCellValue } from './constants';
import { ColumnDef } from './types';
import { InfiniteScrollObserver } from '../../helpers/InfiniteScrollObserver';

type ResultsTableProps = {
  hasAppliedFilters: boolean;
  isLoading: boolean;
  isFetching: boolean;
  visibleColumns: ColumnDef[];
  results: KatanaPathDumpRow[];
  canLoadMore: boolean;
  onLoadMore: () => void;
  onDisplayedSourcePathIdsChange?: (ids: number[]) => void;
};

type SortState = {
  key: ColumnDef['key'] | null;
  direction: 'asc' | 'desc';
};

const toNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const asComparableValue = (row: KatanaPathDumpRow, key: ColumnDef['key']) => {
  const value = row[key] as unknown;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  return formatCellValue(value).toLowerCase();
};

export const ResultsTable: React.FC<ResultsTableProps> = React.memo(
  ({
    hasAppliedFilters,
    isLoading,
    isFetching,
    visibleColumns,
    results,
    canLoadMore,
    onLoadMore,
    onDisplayedSourcePathIdsChange,
  }) => {
    const scrollRootRef = useRef<HTMLDivElement | null>(null);
    const [sortState, setSortState] = useState<SortState>({
      key: null,
      direction: 'asc',
    });
    const [columnFilters, setColumnFilters] = useState<
      Partial<Record<ColumnDef['key'], string>>
    >({});

    const filteredRows = useMemo(() => {
      if (results.length === 0) return [];

      return results.filter((row) =>
        visibleColumns.every((column) => {
          const filterValue = (columnFilters[column.key] ?? '').trim().toLowerCase();
          if (!filterValue) return true;
          return formatCellValue(row[column.key]).toLowerCase().includes(filterValue);
        }),
      );
    }, [results, visibleColumns, columnFilters]);

    const displayedRows = useMemo(() => {
      if (!sortState.key) return filteredRows;

      const sorted = [...filteredRows];
      sorted.sort((rowA, rowB) => {
        const valueA = asComparableValue(rowA, sortState.key as ColumnDef['key']);
        const valueB = asComparableValue(rowB, sortState.key as ColumnDef['key']);

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          const diff = valueA - valueB;
          return sortState.direction === 'asc' ? diff : -diff;
        }

        const diff = String(valueA).localeCompare(String(valueB));
        return sortState.direction === 'asc' ? diff : -diff;
      });

      return sorted;
    }, [filteredRows, sortState]);

    useEffect(() => {
      if (!onDisplayedSourcePathIdsChange) return;

      const ids = new Set<number>();
      displayedRows.forEach((row) => {
        const sourcePathId =
          toNumericId((row as any).source_path_id) ??
          toNumericId(row.id) ??
          toNumericId(row.path_id);

        if (typeof sourcePathId === 'number') {
          ids.add(sourcePathId);
        }
      });

      onDisplayedSourcePathIdsChange(Array.from(ids));
    }, [displayedRows, onDisplayedSourcePathIdsChange]);

    const hasColumnFilters = Object.values(columnFilters).some((value) =>
      Boolean(value?.trim()),
    );

    const updateSort = (key: ColumnDef['key']) => {
      setSortState((prev) => {
        if (prev.key !== key) {
          return { key, direction: 'asc' };
        }
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      });
    };

    const bottomStatusText = (() => {
      if (!hasAppliedFilters || isLoading || results.length === 0) return null;
      if (isFetching) return 'Loading more rows...';
      if (canLoadMore) return 'Scroll to the bottom to load more rows.';
      return 'All available rows are loaded.';
    })();

    return (
      <>
        {hasAppliedFilters && !isLoading && results.length > 0 && (
          <div className="small text-muted mb-2">
            Showing {displayedRows.length} of {results.length} rows on this page.
          </div>
        )}
        <div ref={scrollRootRef} style={{ overflow: 'auto', maxHeight: '65vh' }}>
          <table className="table table-sm table-striped table-bordered mb-0">
            <thead
              className="table-light"
              style={{ position: 'sticky', top: 0, zIndex: 2 }}
            >
              <tr>
                {visibleColumns.map((column) => {
                  const isActiveSort = sortState.key === column.key;
                  const sortIndicator = isActiveSort
                    ? sortState.direction === 'asc'
                      ? ' ▲'
                      : ' ▼'
                    : '';
                  return (
                    <th key={column.key}>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={() => updateSort(column.key)}
                        title={`Sort by ${column.label}`}
                      >
                        {column.label}
                        {sortIndicator}
                      </button>
                    </th>
                  );
                })}
              </tr>
              <tr>
                {visibleColumns.map((column) => (
                  <th key={`${column.key}-filter`}>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      autoComplete="off"
                      name={`rows-filter-${String(column.key)}`}
                      value={columnFilters[column.key] ?? ''}
                      onChange={(evt) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          [column.key]: evt.target.value,
                        }))
                      }
                      placeholder={`Filter ${column.label}`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!hasAppliedFilters && (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center text-muted py-3">
                    Configure filters and click Get Paths to load data.
                  </td>
                </tr>
              )}

              {hasAppliedFilters && !isLoading && !isFetching && results.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center text-muted py-3">
                    No rows found for the current filter set.
                  </td>
                </tr>
              )}

              {hasAppliedFilters &&
                !isLoading &&
                results.length > 0 &&
                displayedRows.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length} className="text-center text-muted py-3">
                      {hasColumnFilters
                        ? 'No rows on this page match the column filters.'
                        : 'No rows to display.'}
                    </td>
                  </tr>
                )}

              {displayedRows.map((row, index) => (
                <tr key={`${row.path_id}-${row.link_order}-${index}`}>
                  {visibleColumns.map((column) => (
                    <td key={column.key}>{formatCellValue(row[column.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {hasAppliedFilters && canLoadMore && (
            <InfiniteScrollObserver
              height={24}
              options={{
                root: scrollRootRef.current,
                rootMargin: '0px 0px 120px 0px',
              }}
              onScrollIn={() => {
                if (!isFetching) {
                  onLoadMore();
                }
              }}
            />
          )}

          {bottomStatusText && (
            <div className="small text-muted text-center py-2">{bottomStatusText}</div>
          )}
        </div>
      </>
    );
  },
);

ResultsTable.displayName = 'ResultsTable';
